const STORE_TIME_ZONE = 'Europe/Oslo';

/**
 * Reads the current date/time in the store's timezone regardless of the
 * visitor's own timezone, since the 10:00 cutoff is a store-operations
 * cutoff, not a personal one.
 * @returns {{ year: number, month: number, day: number, hour: number }}
 */
function getStoreNow() {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: STORE_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    hour12: false,
  }).formatToParts(new Date());

  const lookup = Object.fromEntries(parts.map((part) => [part.type, part.value]));

  return {
    year: Number(lookup.year),
    month: Number(lookup.month),
    day: Number(lookup.day),
    // Midnight is reported as "24" by some engines with hour12: false.
    hour: Number(lookup.hour) % 24,
  };
}

/**
 * Computes the Gregorian Easter Sunday date for a given year (anonymous
 * Gregorian algorithm), used to derive Norway's movable public holidays.
 * @param {number} year
 * @returns {Date} UTC midnight date of Easter Sunday.
 */
function getEasterSunday(year) {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;

  return new Date(Date.UTC(year, month - 1, day));
}

/** @param {Date} date @param {number} days */
function addDays(date, days) {
  const next = new Date(date);
  next.setUTCDate(next.getUTCDate() + days);
  return next;
}

/**
 * Norway's public holidays for a given year, as UTC-midnight Dates.
 * @param {number} year
 * @returns {Date[]}
 */
function getNorwegianHolidays(year) {
  const easter = getEasterSunday(year);

  return [
    new Date(Date.UTC(year, 0, 1)), // Nyttårsdag
    addDays(easter, -3), // Skjærtorsdag
    addDays(easter, -2), // Langfredag
    easter, // 1. påskedag
    addDays(easter, 1), // 2. påskedag
    new Date(Date.UTC(year, 4, 1)), // Arbeidernes dag
    new Date(Date.UTC(year, 4, 17)), // Grunnlovsdagen
    addDays(easter, 39), // Kristi himmelfartsdag
    addDays(easter, 49), // 1. pinsedag
    addDays(easter, 50), // 2. pinsedag
    new Date(Date.UTC(year, 11, 25)), // 1. juledag
    new Date(Date.UTC(year, 11, 26)), // 2. juledag
  ];
}

/** @param {Date} date @param {Date[]} holidays */
function isBusinessDay(date, holidays) {
  const day = date.getUTCDay();
  if (day === 0 || day === 6) return false;

  return !holidays.some((holiday) => holiday.getTime() === date.getTime());
}

/** @param {Date} date @param {Date[]} holidays */
function nextBusinessDay(date, holidays) {
  let next = addDays(date, 1);
  while (!isBusinessDay(next, holidays)) {
    next = addDays(next, 1);
  }
  return next;
}

/**
 * Adds a number of business days on top of a starting date (which is
 * itself assumed to already be a business day, e.g. the ship date).
 * @param {Date} date @param {number} count @param {Date[]} holidays
 */
function addBusinessDays(date, count, holidays) {
  let result = date;
  for (let i = 0; i < count; i += 1) {
    result = nextBusinessDay(result, holidays);
  }
  return result;
}

/**
 * @param {number} cutoffHour
 * @returns {Date} The ship date, guaranteed to be a business day.
 */
function getShipDate(cutoffHour) {
  const { year, month, day, hour } = getStoreNow();
  const today = new Date(Date.UTC(year, month - 1, day));
  const holidays = [...getNorwegianHolidays(year), ...getNorwegianHolidays(year + 1)];

  if (isBusinessDay(today, holidays) && hour < cutoffHour) {
    return today;
  }

  return nextBusinessDay(today, holidays);
}

/** @param {Date} date @param {string} locale */
function formatDay(date, locale) {
  return new Intl.DateTimeFormat(locale, { day: '2-digit', timeZone: 'UTC' }).format(date);
}

/** @param {Date} date @param {string} locale */
function formatMonthYear(date, locale) {
  return new Intl.DateTimeFormat(locale, { month: 'short', year: 'numeric', timeZone: 'UTC' }).format(date);
}

/**
 * Formats a delivery window as "07 - 10 aug, 2026", or "30 aug - 02 sep,
 * 2026" when the range spans a month boundary.
 * @param {Date} start @param {Date} end @param {string} locale
 */
function formatRange(start, end, locale) {
  const sameMonth = start.getUTCMonth() === end.getUTCMonth() && start.getUTCFullYear() === end.getUTCFullYear();
  const endLabel = `${formatDay(end, locale)} ${formatMonthYear(end, locale)}`;

  if (sameMonth) {
    return `${formatDay(start, locale)} - ${endLabel}`;
  }

  const startLabel = `${formatDay(start, locale)} ${new Intl.DateTimeFormat(locale, { month: 'short', timeZone: 'UTC' }).format(start)}`;
  return `${startLabel} - ${endLabel}`;
}

const PLACEHOLDER = '…';

class DeliveryEstimate extends HTMLElement {
  /** @type {MutationObserver | undefined} */
  #observer;

  connectedCallback() {
    this.#update();

    // The quick-view modal reuses/patches an existing dialog's content via a
    // DOM morph when switching between products, rather than inserting a
    // fresh element each time — which means connectedCallback doesn't fire
    // again, and the placeholder text morphed in for the new product is
    // never replaced with a real date. Watch for that and recompute.
    this.#observer ??= new MutationObserver(() => {
      const output = this.querySelector('[ref="deliveryRange"]');
      if (output && output.textContent?.trim() === PLACEHOLDER) this.#update();
    });
    this.#observer.observe(this, { childList: true, subtree: true, characterData: true });
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }

  #update() {
    const output = this.querySelector('[ref="deliveryRange"]');
    if (!output) return;

    const cutoffHour = Number(this.dataset.cutoffHour) || 10;
    const minTransitDays = Number(this.dataset.minTransitDays) || 1;
    const maxTransitDays = Number(this.dataset.maxTransitDays) || 3;
    const locale = document.documentElement.lang || 'en';

    const shipDate = getShipDate(cutoffHour);
    const holidays = [
      ...getNorwegianHolidays(shipDate.getUTCFullYear()),
      ...getNorwegianHolidays(shipDate.getUTCFullYear() + 1),
    ];
    const rangeStart = addBusinessDays(shipDate, minTransitDays, holidays);
    const rangeEnd = addBusinessDays(shipDate, maxTransitDays, holidays);

    output.textContent = formatRange(rangeStart, rangeEnd, locale);
  }
}

if (!customElements.get('delivery-estimate')) {
  customElements.define('delivery-estimate', DeliveryEstimate);
}
